import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  ArrowLeft, Play, FolderOpen, File, FileCode, FileText, Plus, Trash2,
  Send, Loader2, Sparkles, Eye, Code, Download, RefreshCw, X, Maximize2,
  Minimize2, Terminal, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_MAP = {
  'js': 'javascript', 'jsx': 'javascript', 'ts': 'typescript', 'tsx': 'typescript',
  'py': 'python', 'html': 'html', 'css': 'css', 'json': 'json', 'md': 'markdown',
  'sql': 'sql', 'sh': 'shell', 'yml': 'yaml', 'yaml': 'yaml', 'xml': 'xml',
  'txt': 'text', 'env': 'text', 'gitignore': 'text', 'svg': 'xml'
};

function getLanguage(filename) {
  const ext = filename?.split('.').pop()?.toLowerCase() || '';
  return LANG_MAP[ext] || 'text';
}

function getFileIcon(filename) {
  const lang = getLanguage(filename);
  if (['javascript', 'typescript', 'python', 'shell'].includes(lang)) return <FileCode size={13} className="text-yellow-400/60" />;
  if (lang === 'html') return <FileCode size={13} className="text-orange-400/60" />;
  if (lang === 'css') return <FileCode size={13} className="text-blue-400/60" />;
  if (lang === 'json') return <FileCode size={13} className="text-green-400/60" />;
  if (lang === 'markdown') return <FileText size={13} className="text-white/40" />;
  return <File size={13} className="text-white/30" />;
}

export default function DevEnvironment() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [openTabs, setOpenTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [fileContents, setFileContents] = useState({});
  const [unsavedFiles, setUnsavedFiles] = useState(new Set());
  const [showPreview, setShowPreview] = useState(true);
  const [showConsole, setShowConsole] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [consoleLogs, setConsoleLogs] = useState([]);
  const [showNewFile, setShowNewFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [wizardDesc, setWizardDesc] = useState('');
  const [wizardType, setWizardType] = useState('web');
  const [generating, setGenerating] = useState(false);
  const [fullscreenPreview, setFullscreenPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [convId, setConvId] = useState(null);
  const saveTimerRef = useRef(null);
  const previewTimerRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    loadProject();
    loadFiles();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const p = await api.getProject(projectId);
      setProject(p);
    } catch (e) {
      toast.error('Progetto non trovato');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      const artifacts = await api.listArtifacts(projectId);
      const devFiles = artifacts.filter(a => a.type === 'app_file' || a.type === 'code');
      setFiles(devFiles);
      if (devFiles.length === 0) {
        setShowWizard(true);
      }
    } catch (e) { console.error(e); }
  };

  // Open file in tab
  const openFile = async (file) => {
    if (!openTabs.find(t => t.id === file.id)) {
      try {
        const full = await api.getArtifact(projectId, file.id);
        setFileContents(prev => ({ ...prev, [file.id]: full.content || '' }));
        setOpenTabs(prev => [...prev, full]);
      } catch (e) { toast.error('Errore'); return; }
    }
    setActiveTab(file.id);
  };

  // Close tab
  const closeTab = (fileId, e) => {
    e?.stopPropagation();
    if (unsavedFiles.has(fileId) && !window.confirm('File non salvato. Chiudere?')) return;
    setOpenTabs(prev => prev.filter(t => t.id !== fileId));
    setFileContents(prev => { const n = {...prev}; delete n[fileId]; return n; });
    setUnsavedFiles(prev => { const n = new Set(prev); n.delete(fileId); return n; });
    if (activeTab === fileId) {
      const remaining = openTabs.filter(t => t.id !== fileId);
      setActiveTab(remaining.length > 0 ? remaining[remaining.length - 1].id : null);
    }
  };

  // Auto-save with debounce
  const handleEditorChange = useCallback((value, fileId) => {
    setFileContents(prev => ({ ...prev, [fileId]: value || '' }));
    setUnsavedFiles(prev => new Set(prev).add(fileId));
    
    // Debounced auto-save
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      autoSave(fileId, value || '');
    }, 1500);
    
    // Debounced preview refresh
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      rebuildPreview();
    }, 800);
  }, []);

  const autoSave = async (fileId, content) => {
    const file = openTabs.find(t => t.id === fileId);
    if (!file) return;
    try {
      await api.deleteArtifact(projectId, fileId);
      const saved = await api.createArtifact(projectId, {
        title: file.title,
        type: file.type || 'app_file',
        language: file.language || getLanguage(file.title),
        content: content
      });
      // Update file id in tabs and contents
      setOpenTabs(prev => prev.map(t => t.id === fileId ? saved : t));
      setFileContents(prev => {
        const n = { ...prev };
        if (fileId !== saved.id) {
          n[saved.id] = n[fileId];
          delete n[fileId];
        }
        return n;
      });
      if (activeTab === fileId) setActiveTab(saved.id);
      setUnsavedFiles(prev => { const n = new Set(prev); n.delete(fileId); return n; });
      setConsoleLogs(prev => [...prev, { type: 'info', text: `Auto-saved: ${file.title}`, time: new Date().toLocaleTimeString() }]);
      loadFiles();
    } catch (e) {
      setConsoleLogs(prev => [...prev, { type: 'error', text: `Save failed: ${file.title} - ${e.message}`, time: new Date().toLocaleTimeString() }]);
    }
  };

  // Build preview from all files
  const rebuildPreview = useCallback(async () => {
    try {
      const allArtifacts = await api.listArtifacts(projectId);
      const appFiles = allArtifacts.filter(a => a.type === 'app_file' || a.type === 'code');
      
      const fullFiles = await Promise.all(
        appFiles.map(async (f) => {
          // Use local content if available (for unsaved changes)
          if (fileContents[f.id] !== undefined) {
            return { ...f, content: fileContents[f.id] };
          }
          try {
            const full = await api.getArtifact(projectId, f.id);
            return full;
          } catch { return { ...f, content: '' }; }
        })
      );

      const htmlFile = fullFiles.find(f => f.title?.includes('index.html') || f.title?.endsWith('.html'));
      const cssFiles = fullFiles.filter(f => f.title?.endsWith('.css'));
      const jsFiles = fullFiles.filter(f => f.title?.endsWith('.js') && !f.title?.includes('server'));
      
      if (htmlFile) {
        let html = htmlFile.content || '';
        const cssContent = cssFiles.map(f => f.content || '').join('\n');
        if (cssContent) {
          if (html.includes('</head>')) {
            html = html.replace('</head>', `<style>\n${cssContent}\n</style>\n</head>`);
          } else {
            html = `<style>\n${cssContent}\n</style>\n` + html;
          }
        }
        const jsContent = jsFiles.filter(f => f.id !== htmlFile.id).map(f => f.content || '').join('\n;\n');
        if (jsContent) {
          if (html.includes('</body>')) {
            html = html.replace('</body>', `<script>\n${jsContent}\n</script>\n</body>`);
          } else {
            html += `\n<script>\n${jsContent}\n</script>`;
          }
        }
        // Inject console capture
        const consoleCapture = `<script>
          (function(){
            var origLog = console.log, origErr = console.error, origWarn = console.warn;
            function send(type, args) {
              try { window.parent.postMessage({type:'console',level:type,text:Array.from(args).map(a=>typeof a==='object'?JSON.stringify(a):String(a)).join(' ')}, '*'); } catch(e){}
            }
            console.log = function() { send('log', arguments); origLog.apply(console, arguments); };
            console.error = function() { send('error', arguments); origErr.apply(console, arguments); };
            console.warn = function() { send('warn', arguments); origWarn.apply(console, arguments); };
            window.onerror = function(msg,url,line) { send('error', ['Error: '+msg+' (line '+line+')']); };
          })();
        </script>`;
        if (html.includes('<head>')) {
          html = html.replace('<head>', '<head>' + consoleCapture);
        } else {
          html = consoleCapture + html;
        }
        setPreviewHtml(html);
      } else if (fullFiles.length > 0) {
        const content = fullFiles.map(f => `// === ${f.title} ===\n${f.content || ''}`).join('\n\n');
        setPreviewHtml(`<!DOCTYPE html><html><head><style>body{background:#111;color:#ccc;font-family:monospace;padding:20px;font-size:13px;white-space:pre-wrap;}</style></head><body>${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</body></html>`);
      }
    } catch (e) { console.error(e); }
  }, [projectId, fileContents]);

  // Listen for console messages from iframe
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'console') {
        setConsoleLogs(prev => [...prev.slice(-100), {
          type: event.data.level,
          text: event.data.text,
          time: new Date().toLocaleTimeString()
        }]);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (files.length > 0) rebuildPreview();
  }, [files.length]);

  // Create new file
  const handleCreateFile = async () => {
    if (!newFileName.trim()) return;
    try {
      const artifact = await api.createArtifact(projectId, {
        title: newFileName.trim(),
        type: 'app_file',
        language: getLanguage(newFileName.trim()),
        content: ''
      });
      setShowNewFile(false);
      setNewFileName('');
      await loadFiles();
      openFile(artifact);
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteFile = async (file, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Eliminare ${file.title}?`)) return;
    try {
      await api.deleteArtifact(projectId, file.id);
      closeTab(file.id);
      loadFiles();
    } catch (e) { toast.error(e.message); }
  };

  // IAIA Chat - can edit files
  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const text = chatInput.trim();
    setChatInput('');
    setChatLoading(true);
    setChatMessages(prev => [...prev, { role: 'user', content: text }]);

    try {
      let activeConvId = convId;
      if (!activeConvId) {
        const conv = await api.createProjectConversation(projectId);
        activeConvId = conv.id;
        setConvId(conv.id);
      }

      // Build context with ALL file contents
      const fileList = files.map(f => f.title).join(', ');
      const activeFile = openTabs.find(t => t.id === activeTab);
      const activeContent = activeTab ? fileContents[activeTab] || '' : '';
      
      let contextText = `[Progetto: ${project?.name}]\n[File nel progetto: ${fileList}]\n`;
      if (activeFile) {
        contextText += `[File attivo: ${activeFile.title}]\n\`\`\`${activeFile.language || getLanguage(activeFile.title)}\n${activeContent}\n\`\`\`\n`;
      }
      contextText += `\nRichiesta: ${text}\n\nSe modifichi il codice, rispondi con il codice completo del file in un blocco di codice. Se crei un nuovo file, indica il nome all'inizio.`;

      const result = await api.sendMessage(activeConvId, contextText, 'gpt-4o');
      const response = result.assistant_message.content;
      setChatMessages(prev => [...prev, { role: 'assistant', content: response }]);

      // Auto-detect and apply code changes
      const codeBlocks = [...response.matchAll(/```(\w*)\n([\s\S]*?)```/g)];
      if (codeBlocks.length > 0 && activeFile) {
        const mainBlock = codeBlocks[0];
        const newCode = mainBlock[2].trim();
        if (newCode.length > 5) {
          setFileContents(prev => ({ ...prev, [activeTab]: newCode }));
          setUnsavedFiles(prev => new Set(prev).add(activeTab));
          setConsoleLogs(prev => [...prev, { type: 'info', text: `IAIA ha aggiornato: ${activeFile.title}`, time: new Date().toLocaleTimeString() }]);
          // Auto-save and refresh
          setTimeout(() => {
            autoSave(activeTab, newCode);
            rebuildPreview();
          }, 500);
        }
      }
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Errore: ' + e.message }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Wizard: Generate app from scratch
  const handleGenerateApp = async () => {
    if (!wizardDesc.trim()) return;
    setGenerating(true);
    setShowWizard(false);
    setConsoleLogs([{ type: 'info', text: 'IAIA sta generando il progetto...', time: new Date().toLocaleTimeString() }]);
    setShowConsole(true);
    try {
      const result = await api.generateApp(projectId, {
        description: wizardDesc.trim(),
        app_type: wizardType
      });
      setConsoleLogs(prev => [...prev, { type: 'success', text: `Generati ${result.total_files} file!`, time: new Date().toLocaleTimeString() }]);
      toast.success(`${result.total_files} file generati da IAIA!`);
      await loadFiles();
      rebuildPreview();
      // Auto-open first HTML file
      const newFiles = result.files || [];
      const htmlFile = newFiles.find(f => f.title?.includes('.html'));
      if (htmlFile) openFile(htmlFile);
    } catch (e) {
      setConsoleLogs(prev => [...prev, { type: 'error', text: `Errore: ${e.message}`, time: new Date().toLocaleTimeString() }]);
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleExportZip = () => {
    const token = localStorage.getItem('toyou_token');
    fetch(`${API_URL}/api/projects/${projectId}/export.zip`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${project?.name?.replace(/\s+/g, '_') || 'project'}.zip`;
        a.click();
      })
      .catch(() => toast.error('Errore nel download'));
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
      <Loader2 className="animate-spin text-white/40" size={24} />
    </div>
  );

  const activeFile = openTabs.find(t => t.id === activeTab);

  return (
    <TooltipProvider>
      <div className="h-screen flex flex-col bg-[#0B0B0C] overflow-hidden" data-testid="dev-environment">
        {/* ═══ TOP BAR ═══ */}
        <div className="h-10 flex items-center px-3 border-b border-white/[0.06] bg-[#0e0e11] gap-2 flex-shrink-0">
          <Link to={`/projects/${projectId}`} className="text-white/30 hover:text-white/60 transition-colors">
            <ArrowLeft size={15} />
          </Link>
          <div className="h-4 w-px bg-white/[0.08]" />
          <FolderOpen size={13} className="text-white/25" />
          <span className="text-white/60 text-xs font-medium truncate max-w-[120px] sm:max-w-none">{project?.name}</span>
          
          <div className="flex-1" />
          
          {generating && (
            <div className="flex items-center gap-1.5 text-xs text-white/40 mr-2">
              <Loader2 size={12} className="animate-spin" /> 
              <span className="hidden sm:inline">IAIA sta generando...</span>
            </div>
          )}
          
          <div className="flex items-center gap-0.5">
            <Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => { rebuildPreview(); toast.success('Preview aggiornata'); }} className="h-7 w-7 text-white/25 hover:text-white/60">
                <Play size={12} />
              </Button>
            </TooltipTrigger><TooltipContent>Esegui / Aggiorna preview</TooltipContent></Tooltip>
            
            <Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => setShowConsole(!showConsole)} 
                className={`h-7 w-7 ${showConsole ? 'text-white bg-white/10' : 'text-white/25 hover:text-white/60'}`}>
                <Terminal size={12} />
              </Button>
            </TooltipTrigger><TooltipContent>Console</TooltipContent></Tooltip>
            
            <Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => setShowChat(!showChat)} 
                className={`h-7 w-7 ${showChat ? 'text-white bg-white/10' : 'text-white/25 hover:text-white/60'}`}>
                <Sparkles size={12} />
              </Button>
            </TooltipTrigger><TooltipContent>IAIA Assistant</TooltipContent></Tooltip>
            
            <Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => setShowPreview(!showPreview)} 
                className={`h-7 w-7 hidden sm:flex ${showPreview ? 'text-white bg-white/10' : 'text-white/25 hover:text-white/60'}`}>
                <Eye size={12} />
              </Button>
            </TooltipTrigger><TooltipContent>Preview</TooltipContent></Tooltip>
            
            <Button variant="ghost" size="sm" onClick={handleExportZip} className="h-7 text-white/25 hover:text-white/60 text-[11px] hidden sm:flex">
              <Download size={11} className="mr-1" /> ZIP
            </Button>
          </div>
        </div>

        {/* ═══ MAIN AREA ═══ */}
        <div className="flex-1 flex overflow-hidden">
          {/* FILE TREE */}
          <div className="w-44 sm:w-52 flex-shrink-0 bg-[#0c0c0f] border-r border-white/[0.06] flex flex-col">
            <div className="px-2 py-1.5 flex items-center justify-between border-b border-white/[0.04]">
              <span className="text-[10px] text-white/25 uppercase tracking-wider font-semibold">Explorer</span>
              <div className="flex gap-0.5">
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => setShowWizard(true)} className="h-5 w-5 text-white/20 hover:text-white/50">
                    <Sparkles size={10} />
                  </Button>
                </TooltipTrigger><TooltipContent>Genera con IAIA</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => setShowNewFile(true)} className="h-5 w-5 text-white/20 hover:text-white/50">
                    <Plus size={10} />
                  </Button>
                </TooltipTrigger><TooltipContent>Nuovo file</TooltipContent></Tooltip>
              </div>
            </div>
            
            {showNewFile && (
              <div className="px-2 py-1.5 border-b border-white/[0.04]">
                <Input value={newFileName} onChange={(e) => setNewFileName(e.target.value)}
                  placeholder="nome.ext" className="h-6 text-[11px] bg-white/[0.04] border-white/[0.06] text-white px-2"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFile(); if (e.key === 'Escape') setShowNewFile(false); }}
                  autoFocus />
              </div>
            )}
            
            <ScrollArea className="flex-1 py-1">
              {files.length === 0 ? (
                <div className="px-3 py-10 text-center">
                  <Sparkles size={20} className="mx-auto text-white/10 mb-2" />
                  <p className="text-[10px] text-white/20 mb-2">Progetto vuoto</p>
                  <Button onClick={() => setShowWizard(true)} size="sm" className="bg-white text-black hover:bg-white/90 text-[10px] h-6 px-3">
                    Genera con IAIA
                  </Button>
                </div>
              ) : (
                <div className="px-1">
                  {files.map(f => (
                    <button key={f.id} onClick={() => openFile(f)}
                      className={`w-full flex items-center gap-1.5 px-2 py-1 rounded text-left text-[11px] group transition-colors ${
                        activeTab === f.id ? 'bg-white/[0.08] text-white' : 'text-white/45 hover:bg-white/[0.03] hover:text-white/65'
                      }`}>
                      {getFileIcon(f.title)}
                      <span className="flex-1 truncate">{f.title}</span>
                      {unsavedFiles.has(f.id) && <div className="h-1.5 w-1.5 rounded-full bg-orange-400" />}
                      <button onClick={(e) => handleDeleteFile(f, e)} 
                        className="opacity-0 group-hover:opacity-100 text-red-400/40 hover:text-red-400 p-0.5">
                        <Trash2 size={9} />
                      </button>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* EDITOR + PREVIEW AREA */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 flex flex-col sm:flex-row overflow-hidden">
              {/* EDITOR COLUMN */}
              <div className={`flex-1 flex flex-col min-w-0 ${showPreview ? 'sm:w-1/2' : ''}`}>
                {/* File tabs */}
                <div className="h-8 flex items-center bg-[#0e0e11] border-b border-white/[0.06] overflow-x-auto">
                  {openTabs.map(tab => (
                    <div key={tab.id} onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 h-full cursor-pointer border-r border-white/[0.04] min-w-fit transition-colors ${
                        activeTab === tab.id ? 'bg-[#0B0B0C] text-white/80' : 'text-white/35 hover:text-white/55 bg-[#0c0c0f]'
                      }`}>
                      {getFileIcon(tab.title)}
                      <span className="text-[11px] whitespace-nowrap">{tab.title}</span>
                      {unsavedFiles.has(tab.id) && <div className="h-1.5 w-1.5 rounded-full bg-orange-400 flex-shrink-0" />}
                      <button onClick={(e) => closeTab(tab.id, e)} className="ml-1 text-white/20 hover:text-white/50">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                  {openTabs.length === 0 && (
                    <div className="px-3 text-[11px] text-white/20">Nessun file aperto</div>
                  )}
                </div>

                {/* Editor */}
                <div className="flex-1">
                  {activeFile ? (
                    <Editor
                      key={activeTab}
                      height="100%"
                      language={getLanguage(activeFile.title)}
                      value={fileContents[activeTab] || ''}
                      onChange={(val) => handleEditorChange(val, activeTab)}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        padding: { top: 8 },
                        renderLineHighlight: 'line',
                        cursorBlinking: 'smooth',
                        smoothScrolling: true,
                        overviewRulerLanes: 0,
                        hideCursorInOverviewRuler: true,
                        scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
                        bracketPairColorization: { enabled: true },
                        autoClosingBrackets: 'always',
                        autoClosingQuotes: 'always',
                        formatOnPaste: true,
                        suggestOnTriggerCharacters: true
                      }}
                    />
                  ) : (
                    <div className="flex-1 h-full flex items-center justify-center bg-[#0B0B0C]">
                      <div className="text-center">
                        <Code size={28} className="mx-auto text-white/8 mb-3" />
                        <p className="text-white/20 text-xs">Apri un file dall'explorer</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* PREVIEW */}
              {showPreview && (
                <div className={`border-l border-white/[0.06] flex flex-col ${
                  fullscreenPreview ? 'fixed inset-0 z-50' : 'sm:w-1/2 h-1/3 sm:h-full'
                }`}>
                  <div className="h-8 flex items-center px-3 bg-[#0e0e11] border-b border-white/[0.06] gap-2 flex-shrink-0">
                    <div className="flex gap-1.5">
                      <div className="h-2.5 w-2.5 rounded-full bg-red-400/60" />
                      <div className="h-2.5 w-2.5 rounded-full bg-yellow-400/60" />
                      <div className="h-2.5 w-2.5 rounded-full bg-green-400/60" />
                    </div>
                    <span className="text-[10px] text-white/25 ml-2">Preview</span>
                    <div className="flex-1" />
                    <Button variant="ghost" size="icon" onClick={rebuildPreview} className="h-6 w-6 text-white/20 hover:text-white/50">
                      <RefreshCw size={10} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setFullscreenPreview(!fullscreenPreview)} className="h-6 w-6 text-white/20 hover:text-white/50">
                      {fullscreenPreview ? <Minimize2 size={10} /> : <Maximize2 size={10} />}
                    </Button>
                  </div>
                  <div className="flex-1 bg-white">
                    <iframe ref={iframeRef} srcDoc={previewHtml} title="Preview" className="w-full h-full border-0" sandbox="allow-scripts allow-modals allow-forms" />
                  </div>
                </div>
              )}
            </div>

            {/* CONSOLE PANEL */}
            {showConsole && (
              <div className="h-36 border-t border-white/[0.06] bg-[#0c0c0f] flex flex-col flex-shrink-0">
                <div className="h-7 flex items-center px-3 border-b border-white/[0.04] gap-2">
                  <Terminal size={11} className="text-white/25" />
                  <span className="text-[10px] text-white/30 uppercase tracking-wider">Console</span>
                  <div className="flex-1" />
                  <button onClick={() => setConsoleLogs([])} className="text-[10px] text-white/20 hover:text-white/40">Clear</button>
                  <button onClick={() => setShowConsole(false)} className="text-white/20 hover:text-white/40"><X size={11} /></button>
                </div>
                <ScrollArea className="flex-1 px-3 py-1">
                  {consoleLogs.length === 0 ? (
                    <div className="text-[11px] text-white/15 py-2">Console vuota</div>
                  ) : (
                    consoleLogs.map((log, i) => (
                      <div key={i} className={`text-[11px] font-mono py-0.5 flex items-start gap-2 ${
                        log.type === 'error' ? 'text-red-400/80' : log.type === 'warn' ? 'text-yellow-400/70' : log.type === 'success' ? 'text-green-400/70' : 'text-white/40'
                      }`}>
                        <span className="text-white/15 flex-shrink-0">{log.time}</span>
                        {log.type === 'error' && <XCircle size={11} className="flex-shrink-0 mt-0.5" />}
                        {log.type === 'success' && <CheckCircle size={11} className="flex-shrink-0 mt-0.5" />}
                        {log.type === 'warn' && <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" />}
                        <span className="break-all">{log.text}</span>
                      </div>
                    ))
                  )}
                </ScrollArea>
              </div>
            )}
          </div>

          {/* IAIA CHAT PANEL */}
          {showChat && (
            <div className="w-full sm:w-72 flex-shrink-0 border-l border-white/[0.06] bg-[#0e0e11] flex flex-col fixed sm:relative inset-0 sm:inset-auto z-50 sm:z-auto">
              <div className="h-8 flex items-center px-3 border-b border-white/[0.06] gap-2">
                <span className="text-[11px] text-white/40" style={{ fontFamily: 'Montserrat' }}>
                  <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
                </span>
                <span className="text-[10px] text-white/20">Code Assistant</span>
                <div className="flex-1" />
                <button onClick={() => setShowChat(false)} className="text-white/20 hover:text-white/40"><X size={12} /></button>
              </div>
              
              <ScrollArea className="flex-1 px-3 py-2">
                {chatMessages.length === 0 ? (
                  <div className="text-center py-6">
                    <Sparkles size={16} className="mx-auto text-white/10 mb-2" />
                    <p className="text-white/20 text-[10px] leading-relaxed">
                      Chiedi a IAIA di modificare il codice, aggiungere feature, fixare bug, o spiegarti qualcosa
                    </p>
                  </div>
                ) : (
                  chatMessages.map((msg, i) => (
                    <div key={i} className={`mb-2 ${msg.role === 'user' ? 'text-right' : ''}`}>
                      <div className={`inline-block max-w-[95%] rounded-lg px-2.5 py-1.5 text-[11px] ${
                        msg.role === 'user' ? 'bg-white/[0.08] text-white/80' : 'bg-[#0B0B0C] border border-white/[0.05] text-white/60'
                      }`}>
                        <pre className="whitespace-pre-wrap font-sans leading-relaxed">{msg.content?.substring(0, 2000)}{msg.content?.length > 2000 ? '...' : ''}</pre>
                      </div>
                    </div>
                  ))
                )}
                {chatLoading && (
                  <div className="flex items-center gap-1.5 text-white/20 text-[11px] py-1">
                    <Loader2 size={11} className="animate-spin" /> IAIA sta pensando...
                  </div>
                )}
              </ScrollArea>
              
              <div className="p-2 border-t border-white/[0.06]">
                <div className="flex gap-1">
                  <Textarea value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                    placeholder="Chiedi a IAIA..."
                    className="min-h-[28px] max-h-[60px] text-[11px] bg-white/[0.03] border-white/[0.06] text-white resize-none placeholder:text-white/20 py-1.5"
                    rows={1} />
                  <Button onClick={handleChatSend} disabled={chatLoading || !chatInput.trim()}
                    className="h-7 w-7 bg-white text-black hover:bg-white/90 flex-shrink-0" size="icon">
                    <Send size={11} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ WIZARD DIALOG ═══ */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="bg-[#141418] border-white/[0.08] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles size={18} />
              <span>Crea App con <span style={{ fontFamily: 'Montserrat' }}><span className="font-medium">IA</span><span className="font-extrabold">IA</span></span></span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <Label className="text-white/60">Tipo di applicazione</Label>
              <Select value={wizardType} onValueChange={setWizardType}>
                <SelectTrigger className="bg-white/[0.04] border-white/[0.06] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1e] border-white/[0.08]">
                  <SelectItem value="web" className="text-white focus:bg-white/[0.06]">Web App (HTML/CSS/JS)</SelectItem>
                  <SelectItem value="landing" className="text-white focus:bg-white/[0.06]">Landing Page</SelectItem>
                  <SelectItem value="dashboard" className="text-white focus:bg-white/[0.06]">Dashboard</SelectItem>
                  <SelectItem value="game" className="text-white focus:bg-white/[0.06]">Mini Game</SelectItem>
                  <SelectItem value="api" className="text-white focus:bg-white/[0.06]">API / Backend</SelectItem>
                  <SelectItem value="fullstack" className="text-white focus:bg-white/[0.06]">Full Stack</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/60">Descrivi la tua app</Label>
              <Textarea value={wizardDesc} onChange={(e) => setWizardDesc(e.target.value)}
                placeholder="Es: Un'app per la gestione delle prenotazioni di un ristorante, con calendario interattivo, lista clienti e possibilit\u00e0 di confermare/cancellare prenotazioni. Design moderno e dark theme."
                className="bg-white/[0.04] border-white/[0.06] text-white min-h-[140px] placeholder:text-white/20 text-sm"
                data-testid="wizard-app-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWizard(false)} className="border-white/10 text-white/50">Annulla</Button>
            <Button onClick={handleGenerateApp} disabled={generating || !wizardDesc.trim()} className="bg-white text-black hover:bg-white/90" data-testid="wizard-generate-button">
              {generating ? <Loader2 size={15} className="animate-spin mr-2" /> : <Sparkles size={15} className="mr-2" />}
              Genera progetto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
